import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { clearOrderItems, setCategories } from "../../Redux/store";
import { getHostelData } from "../../api/services/FetchNodeService";
import { navigateWithHostelParams } from "../../components/NavigateWithHostelParams";
import {
  Button,
  CircularProgress,
  Typography,
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Paper,
} from "@mui/material";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import Swal from "sweetalert2";

export const CategoryButton = ({ icon, label, parentSubCat, hostel_id }) => {
  const [kitchenTime, setKitchenTime] = useState();
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const orderItems = useSelector((state) => state.food.orderItems);

  const fetchHostelData = useCallback(async () => {
    try {
      const response = await getHostelData(`hostel/food-menu/${hostel_id}`);
      console.log(response);
      const hostel = response.data;
      setKitchenTime(hostel?.fnb_kitchen_timings);
    } catch (e) {
      console.log(e);
    }
  }, [hostel_id]);

  useEffect(() => {
    fetchHostelData();
  }, [fetchHostelData]);



const formatFnbTimings = (timings) => {
  if (!timings) return null;

  try {
    const parsedTimings = Array.isArray(timings)
      ? timings[0]
      : JSON.parse(timings)[0];

    const getTime = (period, label) => {
      const timeSlot = parsedTimings[period]?.[0];
      if (!timeSlot || !timeSlot.active) return null;
      if (!timeSlot.open || !timeSlot.close) return null;

      return `${label} : ${timeSlot.open} to ${timeSlot.close}`;
    };

    const formatted = [
      getTime("breakfast", "Breakfast"),
      getTime("lunch", "Lunch"),
      getTime("dinner", "Dinner"),
    ].filter(Boolean); // removes null entries

    return formatted;
  } catch (e) {
    console.error("Error parsing timings:", e);
    return null;
  }
};



  const handleClick = async () => {
    if (
      orderItems.length > 0 &&
      orderItems[0].sub_category_id !== parentSubCat
    ) {
      const result = await Swal.fire({
        title: "Change Category?",
        text: "You have items in your cart from another category. Changing the category will clear your cart. Do you want to proceed?",
        icon: "warning",
        confirmButtonText: "Clear Cart",
        showCancelButton: true,
        cancelButtonText: "Cancel",
        allowOutsideClick: false,
        allowEscapeKey: false,
      });

      if (!result.isConfirmed) {
        return;
      }

      dispatch(clearOrderItems());
      Swal.fire("Cart Cleared", "Your cart has been cleared.", "success");
    }

    setLoading(true);
    try {
      const hostel_id = localStorage.getItem("hostel_id");
      const hostelName = localStorage.getItem("hostel_name");
      if (hostel_id) {
        const response = await getHostelData(
          `account/sub-category/hostel/${hostel_id}/parent_sub_cat/${parentSubCat}`,
          navigate
        );
        const filteredCategories = response.data.filter(
          (category) =>
            category.total_inventory > 0 && category.available === true
        );

        dispatch(setCategories(filteredCategories));
        const categories = filteredCategories;

        navigateWithHostelParams(navigate, `/categories/${parentSubCat}`, {
          hostelName,
          hostel_id,
          
          state: { label, categories },
        });
        localStorage.setItem("LABEL", label);
        localStorage.setItem("parentSubCat", parentSubCat);
      } else {
        console.error("Hostel ID not found in local storage.");
      }
    } catch (error) {
      console.error("Failed to fetch data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Determine if we should show timings based on the category
  const showTimings = parentSubCat === "fnb";
  const timingsList = showTimings ? formatFnbTimings(kitchenTime) : null;

  return (
    <Paper
      elevation={1}
      sx={{
        borderRadius: "16px",
        overflow: "hidden",
        mb: 2,
        backgroundColor: "white",
      }}
    >
      <Button
        fullWidth
        onClick={handleClick}
        disabled={loading}
        sx={{
          display: "flex",
          justifyContent: "space-between",
          padding: "16px",
          textTransform: "none",
          color: "black",
          backgroundColor: "white",
          borderRadius: "16px 16px 0 0",
          "&:hover": {
            backgroundColor: "rgba(0, 0, 0, 0.04)",
          },
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", width: "100%" }}>
          {loading ? <CircularProgress size={24} /> : icon}
          <Typography
            variant="h6"
            sx={{
              ml: 2,
              flexGrow: 1,
              fontWeight: "medium",
              textAlign: "left",
            }}
          >
            {label}
          </Typography>
          <ChevronRightIcon sx={{ color: "#4CAF50" }} />
        </Box>
      </Button>

      {showTimings && timingsList && timingsList.length > 0 && (
        <Box sx={{ px: 3, pb: 3, pt: 1 }}>
          <Divider sx={{ my: 1 }} />
          <Box sx={{ display: "flex", alignItems: "center", mt: 1, mb: 1 }}>
            <AccessTimeIcon sx={{ color: "#FF9800", mr: 1, fontSize: 20 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: "medium" }}>
              Our Timings
            </Typography>
          </Box>
          <List dense sx={{ pt: 0 }}>
            {timingsList.map((timing, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: "20px" }}>
                  <FiberManualRecordIcon
                    sx={{ color: "#FF9800", fontSize: 10 }}
                  />
                </ListItemIcon>
                <ListItemText
                  primary={timing}
                  primaryTypographyProps={{
                    variant: "body2",
                    sx: { fontWeight: "regular" },
                  }}
                />
              </ListItem>
            ))}
          </List>
        </Box>
      )}
    </Paper>
  );
};